import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled
} from '@ant-design/icons'
import type {
  Carer,
  ContingencyPlanDTO,
  Need,
  ResidentItem,
  ResidentNeed
} from '@lmc/ts-common'
import { Popover, TooltipTitle, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import {
  type EthnicityCode,
  EthnicityDescriptionEnum,
  type GenderIdentityCode,
  type MaritalStatusCode,
  MaritalStatusDescriptionEnum,
  type PregnancyStatusCode,
  PregnancyStatusDescriptionEnum,
  type ReligionCode,
  ReligionDescriptionEnum,
  type ResidentFetchItemResponse,
  type SexCode,
  SexDescriptionEnum,
  type SexualOrientationCode,
  SexualOrientationDescriptionEnum
} from '@lmc/validations'
import { isDnacprScheduled } from '@lmc/validations/lib/utils/dnacpr'
import { Tooltip } from 'antd'
import type { BaseOptionType } from 'antd/es/select'
import type { InternalNamePath } from 'antd/lib/form/interface'
import {
  GenderIdentityDisplayLabelEnum,
  MENTAL_CAPACITY_OPTIONS,
  NHS_TRACE_STATUS_MAP
} from 'common/constants'
import { formatAddress, showToast } from 'common/utils'
import dayjs from 'dayjs'
import { capitalize } from 'lodash'
import type { ReactNode, RefObject } from 'react'
import DownloadForm from '../components/DownloadForm'
import {
  ChildrenContainer,
  ExtraText,
  IconContainer,
  LabelContainer
} from '../styledComponents'
import { shouldDisplayDnacpr } from './dnacpr'
import {
  EMAR_CARE_RELATED_FIELDS,
  PRO_CARE_RELATED_FIELDS
} from './profileScreenFields'

type CareField = { key: string; label: string; altLabel?: string }

export const dateFormat = 'DD-MM-YYYY'
export const dateFormatTime = 'DD-MM-YYYY HH:mm'
export const NO_INFORMATION_ENTERED = 'No information entered'
const dnacprTooltip = 'Always refer to the original DNACPR form'

export const getResidentInfoDetail = (
  resident?: ResidentFetchItemResponse,
  isModsDemographicsEnabled?: boolean
) => {
  const name = `${resident?.name?.first} ${resident?.name?.last}`
  const dateOfBirth = dayjs(resident?.dateOfBirth).format('Do MMMM YYYY')
  const age = `(${dayjs().diff(dayjs(resident?.dateOfBirth), 'year')})`

  const genderIdentityCode = resident?.demographics?.genderIdentity as
    | GenderIdentityCode
    | undefined
  const genderDisplay =
    isModsDemographicsEnabled && genderIdentityCode
      ? GenderIdentityDisplayLabelEnum[genderIdentityCode]
      : capitalize(resident?.gender)

  return `${name} • ${dateOfBirth} ${age} • ${genderDisplay}`
}

export const getNHSFormattedNumber = (
  baseValue: string | number,
  isFromCanada?: boolean
) => {
  const value = baseValue.toString()

  if (isFromCanada) {
    return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(
      6,
      9
    )} ${value.slice(9, 12)}`
  }
  return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`
}

export const cleanNHSFormattedNumber = (value?: string) =>
  value?.replaceAll(' ', '')?.replaceAll('_', '')

type ProfileLike = Record<string, any>

const EXCEPTION_KEYS = new Set([
  'dnacpr',
  'dnacprForm',
  'dnacprStartDate',
  'dnacprDecisionMadeDate',
  'dols',
  'dolsStatus',
  'floor',
  'room',
  'building',
  'bed'
])

const getDemographicDescription = <T extends string>(
  value: T | undefined,
  descriptions: Record<T, string>
) => {
  if (!value) {
    return NO_INFORMATION_ENTERED
  }

  return descriptions[value] || NO_INFORMATION_ENTERED
}

const getCareTeamNames = (
  ids: string[] | undefined,
  carers: Carer[] | undefined
) => {
  if (!ids?.length) {
    return NO_INFORMATION_ENTERED
  }

  return ids
    .map((id) => {
      const name = carers?.find((carer) => carer?.id === id)?.name
      return name?.full || `${name?.first} ${name?.last}`
    })
    .join(', ')
}

export const getValue = (
  key: string,
  profile: ProfileLike,
  carers?: Carer[],
  labelValue?: string,
  isFromCanada?: boolean
): string | ReactNode => {
  if (labelValue) {
    return labelValue
  }

  switch (key) {
    case 'carerGender':
      return profile.carerPreferences?.carerGender
        ? capitalize(profile.carerPreferences.carerGender)
        : NO_INFORMATION_ENTERED
    case 'carerNotes':
      return profile.carerPreferences?.carerNotes
        ? capitalize(profile.carerPreferences.carerNotes)
        : NO_INFORMATION_ENTERED
    case 'environmentDescription': {
      const text = profile?.environment?.environmentDescription?.trim()
      return text || NO_INFORMATION_ENTERED
    }
    case 'ethnicity':
      return getDemographicDescription(
        profile.demographics?.ethnicity as EthnicityCode | undefined,
        EthnicityDescriptionEnum
      )
    case 'genderIdentity': {
      const code = profile.demographics?.genderIdentity as
        | GenderIdentityCode
        | undefined
      return code
        ? GenderIdentityDisplayLabelEnum[code]
        : NO_INFORMATION_ENTERED
    }
    case 'householdComposition': {
      const text = profile?.environment?.householdComposition?.trim()
      return text || NO_INFORMATION_ENTERED
    }
    case 'householdEnvironment': {
      const text = profile?.environment?.householdEnvironment?.trim()
      return text || NO_INFORMATION_ENTERED
    }
    case 'maritalStatus':
      return getDemographicDescription(
        profile.demographics?.maritalStatus as MaritalStatusCode | undefined,
        MaritalStatusDescriptionEnum
      )
    case 'mentalCapacityToConsent': {
      const option = MENTAL_CAPACITY_OPTIONS.find(
        (o) => o.value === profile.mentalCapacityToConsent
      )
      return option?.label || NO_INFORMATION_ENTERED
    }
    case 'pregnancyStatus':
      return getDemographicDescription(
        profile.demographics?.pregnancyStatus as
          | PregnancyStatusCode
          | undefined,
        PregnancyStatusDescriptionEnum
      )
    case 'religion':
      return getDemographicDescription(
        profile.demographics?.religion as ReligionCode | undefined,
        ReligionDescriptionEnum
      )
    case 'sex': {
      const code = profile.demographics?.sex as SexCode | undefined
      return code ? SexDescriptionEnum[code] : NO_INFORMATION_ENTERED
    }
    case 'sexualOrientation':
      return getDemographicDescription(
        profile.demographics?.sexualOrientation as
          | SexualOrientationCode
          | undefined,
        SexualOrientationDescriptionEnum
      )
    case 'specialAccessRequirements': {
      const text = profile?.accessInformation?.specialAccessRequirements?.trim()
      return text || NO_INFORMATION_ENTERED
    }
  }

  const value = profile[key]
  if (!value && !EXCEPTION_KEYS.has(key)) {
    return NO_INFORMATION_ENTERED
  }

  switch (key) {
    case 'address':
      return formatAddress(value)
    case 'admissionDate':
      return dayjs(value).format('Do MMMM YYYY HH:mm')
    case 'bed':
      return profile?.location?.bed || NO_INFORMATION_ENTERED
    case 'building':
      return profile?.location?.building || NO_INFORMATION_ENTERED
    case 'carers':
      return profile?.assignAll
        ? 'All carers'
        : getCareTeamNames(profile?.carers, carers)
    case 'dateOfBirth':
      return `${value} (${dayjs().diff(dayjs(value), 'years')})`
    case 'deathDate':
      return value ? dayjs(value).format('YYYY-MM-DD') : NO_INFORMATION_ENTERED
    case 'dnacpr': {
      if (value === null) {
        return 'No information entered '
      }

      const displayDnacpr = shouldDisplayDnacpr(value, profile.dnacprStartDate)
      return `Current status: ${displayDnacpr ? 'Do Not Attempt CPR' : 'Attempt CPR'} `
    }
    case 'dnacprStartDate':
      return value ? dayjs(value).format('DD/MM/YYYY') : NO_INFORMATION_ENTERED
    case 'dnacprDecisionMadeDate':
      return value ? dayjs(value).format('DD/MM/YYYY') : NO_INFORMATION_ENTERED
    case 'dnacprForm':
      if (profile.dnacpr && (value || profile.dnacprFormPhysicalLocation)) {
        if (value) {
          return (
            <DownloadForm
              fileUrl={profile.dnacprForm}
              fileName={`${profile.name.first} ${profile.name.last}'s DNACPR Form`}
            >
              <Text weight="medium">View Form</Text>
            </DownloadForm>
          )
        }

        return <Text weight="medium">Form not uploaded</Text>
      }
      return NO_INFORMATION_ENTERED
    case 'dols':
      return `${value ? 'Yes' : 'No'} `
    case 'dolsStatus':
      return !profile.dolsStatus ? 'No Status' : capitalize(profile.dolsStatus)
    case 'floor':
      return profile?.location?.floor || NO_INFORMATION_ENTERED
    case 'keyworkers':
      return getCareTeamNames(profile?.keyworkers, carers)
    case 'languagesSpoken':
      return value?.length ? value.join(', ') : NO_INFORMATION_ENTERED
    case 'medicalHistory':
      return profile[key]
    case 'name':
      return `${value.first} ${value.last} ${
        profile?.preferredName ? `(${profile?.preferredName})` : ''
      }`
    case 'nationalities':
      return value?.length ? value.join(', ') : NO_INFORMATION_ENTERED
    case 'nhsNumber':
      return getNHSFormattedNumber(value, isFromCanada)
    case 'room':
      return profile?.location?.room || NO_INFORMATION_ENTERED
    case 'zone':
      return `${profile?.zoneTitle}`
    default:
      return capitalize(profile[key])
  }
}

const NhsTraceStatusIcon = ({
  iconType,
  iconColor
}: {
  iconType: string
  iconColor: string
}) => {
  const style = { color: iconColor, fontSize: 14 }
  switch (iconType) {
    case 'check':
      return <CheckCircleFilled style={style} />
    case 'error':
      return <ExclamationCircleFilled style={style} />
    case 'info':
    default:
      return <InfoCircleFilled style={style} />
  }
}

export const getNhsTraceStatusExtra = (
  profile: any,
  statusInfoLabel = 'Status info'
) => {
  const traceStatusCode = profile?.nhsNumberTraceStatus
  if (!traceStatusCode) return undefined
  const traceStatus = NHS_TRACE_STATUS_MAP[traceStatusCode]
  if (!traceStatus) return undefined

  return (
    <span
      style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}
    >
      <NhsTraceStatusIcon
        iconType={traceStatus.iconType}
        iconColor={traceStatus.iconColor}
      />
      <span style={{ color: theme.color.neutral.color3 }}>
        {traceStatus.shortLabel}
      </span>
      <Popover
        trigger={['hover', 'click']}
        overlayStyle={{ maxWidth: 500 }}
        content={
          <>
            <Text>{traceStatus.tooltip}</Text>
            {traceStatus.popoverLink && (
              <a
                href={traceStatus.popoverLink.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 4
                }}
              >
                {traceStatus.popoverLink.label} ›
              </a>
            )}
          </>
        }
      >
        <span
          style={{
            color: theme.color.hover.primary,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          {statusInfoLabel}
          <IconContainer>
            <QuestionMarkIcon color={theme.color.hover.primary} />
          </IconContainer>
        </span>
      </Popover>
    </span>
  )
}

export const getExtra = (
  key: string,
  profile: ResidentItem,
  statusInfoLabel?: string
) => {
  if (key === 'dnacpr') {
    const scheduled = isDnacprScheduled(
      profile?.dnacpr,
      profile?.dnacprStartDate ?? undefined
    )
    if (scheduled && profile?.dnacprStartDate) {
      const start = dayjs(profile.dnacprStartDate).format('DD/MM/YYYY')
      const parts = [`Upcoming DNACPR from ${start}`]
      if (profile.dnacprDetails) {
        parts.push(profile.dnacprDetails)
      }
      if (profile.dnacprReviewDate) {
        parts.push(
          `Review date: ${dayjs(profile.dnacprReviewDate).format('DD/MM/YYYY')}`
        )
      }
      return parts.join(' · ')
    }
    return profile?.dnacprReviewDate
      ? `Review Date: ${dayjs(profile?.dnacprReviewDate).format('DD/MM/YYYY')}`
      : profile?.dnacprDetails
  }

  if (key === 'dnacprForm') {
    return profile?.dnacprFormPhysicalLocation
  }

  if (key === 'dols') {
    const today = dayjs().startOf('day')
    const inputDate = dayjs(profile?.dolsReviewDate).startOf('day')
    const isOverdue = inputDate.isBefore(today)
    if (isOverdue) {
      return 'Review date overdue'
    }
    return profile?.dolsReviewDate
      ? `Review Date: ${dayjs(profile?.dolsReviewDate).format('DD/MM/YYYY')}`
      : 'No date set'
  }

  if (key === 'nhsNumber') {
    return getNhsTraceStatusExtra(profile, statusInfoLabel)
  }

  return undefined
}

export const getProfileViewSectionItems = (
  items: { key: string; label: string; labelValue?: string }[],
  resident: ResidentItem,
  carers?: Carer[],
  isFromCanada?: boolean,
  keyworkersEnabled?: boolean,
  statusInfoLabel?: string
) => {
  const resultItems = items
    ?.filter((item) => {
      if (item.key === 'keyworkers' && !keyworkersEnabled) {
        return false
      }
      return true
    })
    ?.map((field, i) => {
      return {
        key: i,
        label: (
          <LabelContainer>
            <Text>{field?.label}</Text>
            {field.key === 'dnacpr' && (
              <Tooltip
                color={theme.color.neutral.color3}
                placement="topLeft"
                title={<TooltipTitle>{dnacprTooltip}</TooltipTitle>}
              >
                <IconContainer>
                  <QuestionMarkIcon />
                </IconContainer>
              </Tooltip>
            )}
          </LabelContainer>
        ),
        children:
          getValue(
            field.key,
            resident,
            carers,
            field.labelValue || undefined,
            isFromCanada
          ) === NO_INFORMATION_ENTERED ? (
            NO_INFORMATION_ENTERED
          ) : (
            <ChildrenContainer>
              <Text>
                {getValue(
                  field.key,
                  resident,
                  carers,
                  field.labelValue || undefined,
                  isFromCanada
                )}
              </Text>
              {getExtra(field.key, resident, statusInfoLabel) && (
                <ExtraText level={2}>
                  {getExtra(field.key, resident, statusInfoLabel)}
                </ExtraText>
              )}
            </ChildrenContainer>
          )
      }
    })
    .filter((field) => field.children !== NO_INFORMATION_ENTERED)
  return resultItems
}

export const getCareRelatedFields = (
  isPro: boolean,
  isEmar: boolean,
  isFromCanada: boolean,
  isDoLSOverride: boolean,
  hasRota: boolean,
  dolsModsAlignment: boolean = false,
  mentalCapacityEnabled: boolean = false
): CareField[] => {
  let careRelatedFields: CareField[] = []

  const proFields = PRO_CARE_RELATED_FIELDS?.filter((item) =>
    hasRota && isPro
      ? !['fundingSource', 'fundingSourceOther']?.includes(item.key)
      : true
  ).filter((field) => {
    if (dolsModsAlignment && field.key === 'dols') return false
    if (!mentalCapacityEnabled && field.key === 'mentalCapacityToConsent')
      return false
    return true
  })

  if (isPro && isEmar) {
    careRelatedFields = [...proFields, ...EMAR_CARE_RELATED_FIELDS]
  } else if (isPro) {
    careRelatedFields = proFields
  } else if (isEmar) {
    careRelatedFields = EMAR_CARE_RELATED_FIELDS
  }

  if (isDoLSOverride) {
    careRelatedFields = careRelatedFields.map((field) =>
      field.key === 'dols' ? { key: 'dols', label: 'DoLS' } : field
    )
  }

  if (isDoLSOverride) {
    careRelatedFields = careRelatedFields.map((field) =>
      field.key === 'dolsStatus'
        ? { key: 'dolsStatus', label: 'DoLS Status' }
        : field
    )
  }

  if (isFromCanada) {
    careRelatedFields = careRelatedFields
      .filter((field) => field.key !== 'nationalInsurance')
      .map((field) =>
        field.key === 'nhsNumber'
          ? { key: 'nhsNumber', label: 'Health Care Number' }
          : field
      )
  }

  return careRelatedFields
}

export const getPhoneLabelFromValue = (value: string | undefined) => {
  if (!value) {
    return 'New phone number'
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

export const scrollToTop = (topRef: RefObject<HTMLElement>) => {
  if (topRef?.current) {
    topRef?.current?.scrollIntoView({ behavior: 'instant' })
  }
}

export const scrollToSection = (sectionRef: RefObject<HTMLElement>) => {
  if (sectionRef?.current) {
    sectionRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }
}

export const toFeetAndInches = (cm: number) => {
  // 1 foot is 30.48 cm, and 1 inch is 2.54 cm
  const feet = Math.floor(cm / 30.48)
  const inches = Math.round((cm - feet * 30.48) / 2.54)

  return `${feet}'${inches}"`
}

export const getAge = (residentAge: string | undefined) => {
  if (residentAge === undefined) {
    return undefined
  }
  const birthDate = dayjs(residentAge)
  const age = dayjs().diff(birthDate, 'year')
  return age
}

export const getSelectOptions = (
  options: BaseOptionType[]
): BaseOptionType[] => {
  return options.map((option) => ({
    ...option,
    value: option.value,
    label: <Text>{option.label}</Text>
  }))
}

export const getSelectOptionsWithTooltip = (
  options: BaseOptionType[]
): BaseOptionType[] => {
  const selectOptions = getSelectOptions(options)

  const optionsWithTooltip = selectOptions.map((option) => ({
    ...option,
    label: (
      <Tooltip placement="top" title={option.label}>
        {option.label}
      </Tooltip>
    )
  }))

  return optionsWithTooltip
}

export const handleOnFinishFailed = (
  errorFields: {
    name: InternalNamePath
    errors: string[]
  }[]
) =>
  errorFields.map((errorField) =>
    errorField.errors.map((err) => showToast({ title: err, type: 'error' }))
  )

export const getPlanSubtitle = (plan: ContingencyPlanDTO) => {
  return `${plan.editedOn ? `Reviewed ${dayjs(plan.editedOn).format('DD-MM-YYYY')} • ` : ''}${
    plan.editedBy?.name || plan.createdBy?.name
  } • Next review ${dayjs(plan.nextReviewDate).format('DD-MM-YYYY')}`
}

export const isPlanOverdue = (planDate: string) => {
  const inputDate = dayjs(planDate).startOf('day')
  return inputDate.isBefore(dayjs())
}
